import { Injectable, inject } from '@angular/core';
import { Observable, map, tap } from 'rxjs';

import { AuthStore } from './auth.store';
import { CrudService } from './crud.service';
import { User } from './user.interface';

export interface LoginCredentials {
    cpf: string;
    password: string;
    origin?: string;
}

export interface UpdateProfilePayload {
    name: string;
    email: string;
}

export interface ChangePasswordPayload {
    current_password: string;
    password: string;
    password_confirmation: string;
}

interface LoginResponse {
    access_token: string;
    user: User;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly crudService = inject(CrudService);
    private readonly authStore = inject(AuthStore);

    private readonly loginEndpoint = '/auth/login';
    private readonly logoutEndpoint = '/auth/logout';
    private readonly meEndpoint = '/auth/me';
    private readonly profileEndpoint = '/auth/me/profile';
    private readonly changePasswordEndpoint = '/auth/me/password';

    login(credentials: LoginCredentials): Observable<User> {
        return this.crudService.post<LoginResponse, LoginCredentials>(this.loginEndpoint, credentials).pipe(
            tap(({ user, access_token }) => this.authStore.setSession(user, access_token)),
            map(({ user }) => user)
        );
    }

    logout(): Observable<void> {
        return this.crudService.post<null, null>(this.logoutEndpoint, null).pipe(
            tap(() => this.authStore.clear()),
            map(() => undefined)
        );
    }

    me(): Observable<User> {
        return this.crudService.get<User>(this.meEndpoint).pipe(
            tap((user) => {
                const token = this.authStore.token();

                if (token) {
                    this.authStore.setSession(user, token);
                }
            })
        );
    }

    updateProfile(payload: UpdateProfilePayload): Observable<User> {
        return this.crudService.post<User, UpdateProfilePayload>(this.profileEndpoint, payload).pipe(
            tap((user) => {
                const token = this.authStore.token();

                if (token) {
                    this.authStore.setSession(user, token);
                }
            })
        );
    }

    changePassword(payload: ChangePasswordPayload): Observable<void> {
        return this.crudService.post<null, ChangePasswordPayload>(this.changePasswordEndpoint, payload).pipe(map(() => undefined));
    }
}
